import { useState } from 'react';
import { useSuspenseAPIKeyQuery } from '#api/api-keys.queries.ts';
import { ValidationError } from '#api/errors.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useDeleteAPIKeyMutation } from './useDeleteAPIKeyMutation.ts';

type Props = {
  apiKeyID: string;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteAPIKeyDialog({ apiKeyID, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteAPIKey = useDeleteAPIKeyMutation();
  const apiKeyQuery = useSuspenseAPIKeyQuery(apiKeyID);
  const apiKey = apiKeyQuery.data.data;

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Revoke {apiKey.name}?</div>}
      disableAccept={deleteAPIKey.isPending || !apiKey}
      acceptButtonText="Revoke"
      onCancel={closeModal}
      onAccept={async () => {
        // Clear out the validation error messages
        setValidationErrors(null);

        try {
          await deleteAPIKey.mutateAsync(apiKeyID);
          onDelete();
        } catch (error: unknown) {
          if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mt-0">
        If you revoke this API key, anything using it will no longer be able to communicate with the Defined Networking
        API.
      </p>
      <p className="p mb-0">
        Are you sure you want to revoke <Code className="ml-6 wrap-anywhere">{apiKey.name}</Code>{' '}
        <Code className="ml-0">({apiKey.id})</Code>?
      </p>

      {validationErrors?.length ?
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))
      : null}
    </ConfirmDialog>
  );
}
